import { isViaModelSeries, isViaPlants } from '@/containers/CampaignDetail/AffectedVehicles/SeriesPanel/SeriesRow/series-row-utils';
import { getAllModelSeries, getSelectedModelSeries } from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/VehicleCleanDirt/VehicleCleanDirt-utils';
import { okNokFields } from '@/containers/CampaignDetail/Translations/translations-utils';
import { isCampaignRDMP, isMbos, isMPDepartment, isValidPartNumber, isValidYWPFortmat, isVansBeforeFlowRemoval } from '@/helpers/utils';
import { descriptionIssues, DOORMAN_PROCESS_KEY, YES, workflowOptions, CR_TYPE, NO, SOFTWARE, RECIPIENTS, DESCRIPTION, CLASSIFICATION, REQUIRED_SFM_LENGHT_NUMBER } from './constants';
import { limitOfVolumeTabsMapping } from './opensearch-utils';

export const checkRequiredError = (validations, value) => ((!value || value?.length === 0)
  ? { invalid: true, errors: { ...validations?.errors, required: true } }
  : { invalid: false, errors: { ...validations?.errors, required: false } });

export const checkMinLengthError = (validations, value, minLength, showMinError) => ((value?.length < minLength)
  ? { invalid: showMinError, errors: { ...validations?.errors, min: showMinError } }
  : { invalid: false, errors: { ...validations?.errors, min: false } });

export const checkMaxLengthError = (validations, value, maxLength) => ((value?.length > maxLength)
  ? { invalid: true, errors: { ...validations?.errors, max: true } }
  : { invalid: false, errors: { ...validations?.errors, max: false } });

export const checkRequiredErrorTranslations = (validations, value, defaultLanguage, lang) => (
  (defaultLanguage !== lang && (!value || value?.length === 0))
    ? { invalid: true, errors: { ...validations?.errors, required: true } }
    : { invalid: false, errors: { ...validations?.errors, required: false } });

export const validateField = (validations, value, errors, showMinError) => {
  let allValidations = validations;

  if (Object.keys(errors).includes('required')) {
    allValidations = checkRequiredError(allValidations, value);
  }

  if (Object.keys(errors).includes('min')) {
    allValidations = checkMinLengthError(allValidations, value, errors?.min, showMinError);
  }

  if (Object.keys(errors).includes('max')) {
    allValidations = checkMaxLengthError(allValidations, value, errors?.max);
  }

  allValidations.invalid = Object.keys(errors).some((type) => allValidations?.errors?.[type]);

  return allValidations;
};

export const defineErrorsTypes = (label) => {
  const comments = [DESCRIPTION.TRIGGER_COMMENT, 'consultationComment', 'comment', DESCRIPTION.INSPECTION_COMMENT];
  const optionalMaxLengthComments = ['vehicleModelDesignationComment', 'aggregateComment', 'specialEquipmentComment',
    'countryVariantsComment', 'paintCodeComment', 'codeRuleComment'];

  if (comments.includes(label)) {
    return { required: true, min: 8, max: 256 };
  }

  if (label === DESCRIPTION.PROBLEMS) {
    return { required: true, min: 8, max: 512 };
  }

  if (label === CLASSIFICATION.RELEVANCE_COMMENT) {
    return { required: true, min: 8, max: 1024 };
  }

  if (label.includes('plantAccountingNumber') || label.includes('plantPsNumber')) {
    return { max: 64 };
  }

  if (optionalMaxLengthComments.includes(label)) {
    return { max: 256 };
  }

  return { required: true };
};

export const getNewState = (validations, label, value, tabSelected, showMinError) => ({
  ...validations,
  [label]: (label === DESCRIPTION.PROBLEMS)
    ? {
      ...validations?.problems,
      [tabSelected]: validateField(validations, value, defineErrorsTypes(label), showMinError),
    }
    : validateField(validations, value, defineErrorsTypes(label), showMinError),
});

export const getNewStateTranslation = (validations, label1, value, label2) => ({
  ...validations,
  [label1]: validateField(validations, value, defineErrorsTypes(label2)),
});

export const validateFieldDependingOfSwitcher = (validations, label, value, switcherValue, showMinError) => {
  if (switcherValue) {
    const inputValue = label === CLASSIFICATION.CONSULTATION_NAME ? value?.summary : value;

    return getNewState(validations, label, inputValue, null, showMinError);
  }
};

export const validateDescriptionForm = (validations, campaign, lang) => {
  const { description } = campaign;
  const isDoormanProcess = description?.source === DOORMAN_PROCESS_KEY;
  const isMP = isMPDepartment(campaign?.info?.creationUser?.department);
  const descriptionFields = [DESCRIPTION.TRIGGER_COMMENT, DESCRIPTION.INSPECTION_COMMENT];
  const helpValues = ['SQMS', 'ZEUS', 'CAT', 'OTHER'];
  let partiesFields = [DESCRIPTION.REF_SYSTEM, DESCRIPTION.IRA_NUMBER, DESCRIPTION.CONTACT_PERSON, DESCRIPTION.REF_SYSTEM_NAME, 'comment'];
  const requiredFalse = { errors: { ...validations?.errors, required: false } };

  let desValid = {
    problems: {},
    source: validateField(validations, description?.source, defineErrorsTypes(DESCRIPTION.SOURCE)),
    failureOccurredOn: validateField(validations, description?.failureOccurredOn, defineErrorsTypes(DESCRIPTION.FAILURE_OCCURRED_ON)),
    referenceSystem: validateField(validations, description?.referencedSystem, defineErrorsTypes(DESCRIPTION.REF_SYSTEM)),
    iraNumber: (isDoormanProcess || ([...helpValues, ...['SAP', 'SBM', 'STARC_GENERATED']].includes(description?.referencedSystem)))
      ? validateField(validations, description?.iraNumber, defineErrorsTypes(DESCRIPTION.IRA_NUMBER))
      : requiredFalse,
    contactPerson: (!isDoormanProcess && helpValues.includes(description?.referencedSystem))
      ? validateField(validations, description?.contactPerson?.summary, defineErrorsTypes(DESCRIPTION.CONTACT_PERSON))
      : requiredFalse,
    referencedSystemName: description?.referencedSystem === 'OTHER'
      ? validateField(validations, description?.referencedSystemName, defineErrorsTypes(DESCRIPTION.REF_SYSTEM_NAME))
      : requiredFalse,
  };

  (description?.plants ?? []).forEach((currentPlant) => {
    const newItems = [`${currentPlant.plantName}_plantPsNumber`, `${currentPlant.plantName}_plantAccountingNumber`];
    desValid = description?.locationInternalIsChosen
      ? {
        ...desValid,
        [newItems[0]]: validateField(validations, currentPlant.plantPsNumber, defineErrorsTypes('plantPsNumber')),
        [newItems[1]]: { errors: { ...validations?.errors, max: false } },
      }
      : {
        ...desValid,
        [newItems[0]]: { errors: { ...validations?.errors, max: false } },
        [newItems[1]]: validateField(validations, currentPlant.plantAccountingNumber, defineErrorsTypes('plantAccountingNumber')),
      };
    partiesFields = partiesFields.concat(newItems);
  });

  descriptionFields.forEach((field) => {
    if ((description?.source === 'OTHER' && field === DESCRIPTION.TRIGGER_COMMENT) || (description?.inspectionType?.length > 0
      && field === DESCRIPTION.INSPECTION_COMMENT)) {
      desValid[field] = validateField(validations, description?.[field]?.[lang], defineErrorsTypes(field), true);
    }
  });
  descriptionIssues.forEach((field) => {
    desValid.problems[field] = validateField(validations, description?.problems?.[field]?.[lang], defineErrorsTypes(DESCRIPTION.PROBLEMS), true);
  });

  desValid.locationExternal = {
    contactPersonSupplierManagement: !description.locationInternalIsChosen
      ? validateField(validations, description.locationExternal?.contactPersonSupplierManagement?.summary, defineErrorsTypes(DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT))
      : requiredFalse,
    additionalContactPerson: !description?.locationInternalIsChosen && !isMP
      ? validateField(validations, description?.locationExternal?.additionalContactPerson?.summary, defineErrorsTypes('additionalContactPerson'))
      : requiredFalse,
    costCoverageAcknowledged: description.locationInternalIsChosen ? requiredFalse : validateField(validations, description.locationExternal?.costCoverageAcknowledged, defineErrorsTypes(DESCRIPTION.COST_COVERAGE_ACKNOWLEDGED)),
    tier1SupplierAcknowledged: description.locationInternalIsChosen || !(description.locationExternal?.supplierNumber && description.locationExternal?.supplierName)
      ? requiredFalse
      : validateField(validations, description.locationExternal?.tier1SupplierAcknowledged, defineErrorsTypes(DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_SUPP_ACKN)),
  };
  desValid.locationExternal.invalid = [DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT, DESCRIPTION.COST_COVERAGE_ACKNOWLEDGED, 'additionalContactPerson', DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_SUPP_ACKN]
    .some((field) => desValid.locationExternal[field]?.invalid);

  desValid.locationInternal = {
    possibleCauser: (description.locationInternalIsChosen)
      ? validateField(validations, description.locationInternal?.possibleCauser?.summary, defineErrorsTypes(DESCRIPTION.POSSIBLE_CAUSER))
      : requiredFalse,
    confirmPlantToPlant: (description.locationInternalIsChosen && description.locationInternal?.possibleCauser?.code)
      ? validateField(validations, description.locationInternal?.confirmPlantToPlant, defineErrorsTypes(DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.CONFIRM_PLANT_TO_PLANT))
      : requiredFalse,
  };
  desValid.locationInternal.invalid = [DESCRIPTION.POSSIBLE_CAUSER, DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.CONFIRM_PLANT_TO_PLANT].some((field) => desValid.locationInternal[field]?.invalid);

  desValid.invalid = descriptionIssues.some((field) => desValid.problems?.[field]?.invalid) || desValid.source?.invalid
    || desValid.failureOccurredOn?.invalid || partiesFields.some((field) => desValid[field]?.invalid || desValid.locationExternal?.invalid
      || desValid.locationInternal?.invalid);

  if (description?.source === 'OTHER') {
    desValid.invalid = desValid.invalid || desValid.triggerComment?.invalid;
  }

  if (description?.inspectionType?.length > 0) {
    desValid.invalid = desValid.invalid || desValid.inspectionComment?.invalid;
  }

  return desValid;
};

export const validateClassificationForm = (validations, campaign, lang) => {
  const commonFields = [CLASSIFICATION.RELEVANCE_COMMENT, CLASSIFICATION.CUSTOMER_RELEVANCE, CLASSIFICATION.POTENTIALLY_SCE_RELEVANT];
  const consultationFields = [CLASSIFICATION.CONSULTATION_DATE, CLASSIFICATION.CONSULTATION_NAME, CLASSIFICATION.CONSULTATION_COMMENT];
  const CartClassificationFields = [...consultationFields];
  const developmentVehicleFields = [CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES, CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING];
  const fieldsToValidate = [...commonFields, ...CartClassificationFields, ...developmentVehicleFields];

  const isConsultationActive = campaign.classification?.consultationType
    && campaign.classification.consultationType !== CLASSIFICATION.NO_CONSULTATION;

  const classificationValidation = {
    invalid: false,
    relevanceComment: !campaign.classification?.customerRelevanceChosen
      ? validateField(validations, campaign.classification?.relevanceComment?.[lang], defineErrorsTypes(CLASSIFICATION.RELEVANCE_COMMENT), true)
      : { errors: { ...validations?.errors, required: false } },
    potentiallySCECertificationRelevant: !campaign.classification?.customerRelevanceChosen
      ? validateField(validations, campaign.classification?.potentiallySCECertificationRelevant, defineErrorsTypes(CLASSIFICATION.POTENTIALLY_SCE_RELEVANT))
      : { errors: { ...validations?.errors, required: false } },
    customerRelevance: campaign.classification?.customerRelevanceChosen
      ? validateField(validations, campaign.classification?.customerRelevance, defineErrorsTypes(CLASSIFICATION.CUSTOMER_RELEVANCE))
      : { errors: { ...validations?.errors, required: false } },
    [CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES]: validateField(validations,
      campaign.classification?.crashCertificationVehicles, defineErrorsTypes(CLASSIFICATION.CRASH_CERTIFICATION_VEHICLES)),
    [CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING]: validateField(validations,
      campaign.classification?.developmentVehiclePooling, defineErrorsTypes(CLASSIFICATION.DEVELOPMENT_VEHICLE_POOLING)),
    [CLASSIFICATION.CONSULTATION_DATE]: isConsultationActive
      ? validateField(validations, campaign.classification?.consultationDate, defineErrorsTypes(CLASSIFICATION.CONSULTATION_DATE))
      : { errors: { ...validations?.errors, required: false } },
    [CLASSIFICATION.CONSULTATION_NAME]: isConsultationActive
      ? validateField(validations, campaign.classification?.consultantName?.code, defineErrorsTypes(CLASSIFICATION.CONSULTATION_NAME))
      : { errors: { ...validations?.errors, required: false } },
    [CLASSIFICATION.CONSULTATION_COMMENT]: isConsultationActive
      ? validateField(validations, campaign.classification?.consultationComment?.[lang], defineErrorsTypes(CLASSIFICATION.CONSULTATION_COMMENT), true)
      : { errors: { ...validations?.errors, required: false, min: false } },
  };

  classificationValidation.invalid = fieldsToValidate.some((field) => classificationValidation[field]?.invalid);

  return classificationValidation;
};

const isNotMandatoryValidFormat = (ywpVersionValue) => ywpVersionValue.length === 0 || (ywpVersionValue.length !== 0 && isValidYWPFortmat(ywpVersionValue));
const getPartNumberValidation = (newPartNumber, isMbosRelevant) => ({
  isValid: isMbosRelevant ? isValidPartNumber(newPartNumber) : !!newPartNumber,
  error: isMbosRelevant ? 'dialog.wrongPartNoFormat' : 'validations.required',
});

export const validateCodingTable = (software) => {
  const { codingParts, otaUpdate, coding, mbosDetails, codingUuids } = software;
  const isMbosRelevant = isMbos(mbosDetails, coding);

  const codingValidations = () => {
    const uuidFlashableOta = () => {
      if (codingUuids.length > 0) {
        return codingUuids?.reduce((prev, { diogenesName, uuidCodeOld }, index) => ({
          ...prev,
          [index]: ({
            uuidCodeOld: { isValid: !!uuidCodeOld, error: 'validations.required' },
            diogenesName: { isValid: !!diogenesName, error: 'validations.required' },
          }),
        }), {});
      }

      return {
        uuidCodeOld: { isValid: !!codingUuids.uuidCodeOld, error: 'validations.required' },
        diogenesName: { isValid: !!codingUuids.diogenesName, error: 'validations.required' },
      };
    };

    const partsConditions = () => {
      return codingParts?.reduce((prev, { numberNew, numberOld, diogenesName, hwPartNo, ywpVersionOld, ywpVersionNew }, index) => ({
        ...prev,
        [index]: ((otaUpdate === YES && coding)
          && {
          numberNew: getPartNumberValidation(numberNew, isMbosRelevant),
          diogenesName: { isValid: !!diogenesName, error: 'validations.required' },
          hwPartNo: { isValid: !!hwPartNo, error: 'validations.required' },
        },
          (coding && isMbosRelevant && mbosDetails.releaseFlashable === YES)
          && {
            numberNew: getPartNumberValidation(numberNew, isMbosRelevant),
            numberOld: { isValid: !!numberOld, error: 'validations.required' },
            ywpVersionOld: { isValid: isNotMandatoryValidFormat(ywpVersionOld), error: 'validations.required' },
            ywpVersionNew: { isValid: isNotMandatoryValidFormat(ywpVersionNew), error: 'validations.required' },
          },
          (!otaUpdate === YES && coding && isMbosRelevant && mbosDetails.releaseFlashable === YES) && {
            ywpVersionOld: { isValid: isNotMandatoryValidFormat(ywpVersionOld), error: 'validations.required' },
            ywpVersionNew: { isValid: isNotMandatoryValidFormat(ywpVersionNew), error: 'validations.required' },
            ...(isMbosRelevant && {
              numberNew: getPartNumberValidation(numberNew, isMbosRelevant),
            }),
          }),
      }), {});
    };

    if (coding && isMbosRelevant && mbosDetails.releaseFlashable === YES) {
      return uuidFlashableOta();
    } else {
      return partsConditions();
    }
  };

  const rowsValidation = Object.values(codingValidations()).reduce((prev, current, index) => ({
    ...prev,
    [index]: { invalid: Object.values(current)?.some(({ isValid }) => !isValid) },
  }), {});

  return {
    invalid: Object.values(rowsValidation)?.some(({ invalid }) => invalid),
    validations: codingValidations(),
  };
};

export const validateBlockVehicleTable = (software) => {
  const { doormanParts, doormanSelected } = software;
  const isDoormanTableEmpty = doormanSelected && doormanParts.length === 0;
  const blockVehicleValidations = doormanSelected
    ? doormanParts.reduce((prev, { doormanBundleId, partNaming, partNumber }, index) => ({
      ...prev,
      [index]: {
        doormanBundleId: { isValid: !!doormanBundleId, error: 'validations.required' },
        partNaming: { isValid: !!partNaming, error: 'validations.required' },
        partNumber: { isValid: !!partNumber, error: 'validations.required' },
      },
    }), {})
    : {};

  const rowsValidation = Object.values(blockVehicleValidations).reduce((prev, current, index) => ({
    ...prev,
    [index]: { invalid: Object.values(current).some(({ isValid }) => !isValid) },
  }), {});

  return {
    invalid: isDoormanTableEmpty || Object.values(rowsValidation).some(({ invalid }) => invalid),
    validations: blockVehicleValidations,
  };
};

export const validateFlashingTable = (software) => {
  const { flashingParts, otaUpdate, flashing, mbosDetails } = software;
  const isMbosRelevant = isMbos(mbosDetails);
  const flashingValidations = flashingParts.reduce((prev, { numberNew, numberOld, diogenesName, hwPartNo, ywpVersionOld, ywpVersionNew }, index) => ({
    ...prev,
    [index]: (flashing && otaUpdate === YES && !isMbosRelevant)
      ? {
        numberOld: { isValid: !!numberOld, error: 'validations.required' },
        numberNew: getPartNumberValidation(numberNew, isMbosRelevant),
        diogenesName: { isValid: !!diogenesName, error: 'validations.required' },
        hwPartNo: { isValid: !!hwPartNo, error: 'validations.required' },
      }
      : {
        ...((flashing && isMbosRelevant)
          ? { numberOld: { isValid: !!numberOld, error: 'validations.required' } }
          : {
            ywpVersionOld: { isValid: isNotMandatoryValidFormat(ywpVersionOld), error: 'validations.required' },
            ywpVersionNew: { isValid: isNotMandatoryValidFormat(ywpVersionNew), error: 'validations.required' },
          }),
        ...(numberNew?.length && {
          numberNew: getPartNumberValidation(numberNew, true),
        }),
      },
  }), {});

  const rowsValidation = Object.values(flashingValidations).reduce((prev, current, index) => ({
    ...prev,
    [index]: { invalid: Object.values(current).some(({ isValid }) => !isValid) },
  }), {});

  return {
    invalid: Object.values(rowsValidation).some(({ invalid }) => invalid),
    validations: flashingValidations,
  };
};

export const validateMBOSDetails = (software) => {
  const { mbosDetails, flashing, coding } = software;
  const { isMbos, releaseFlashable, stillFlashable } = mbosDetails;
  const flashingOrCoding = flashing || coding;
  const mbosValidations = {
    isMbos: { invalid: flashingOrCoding && isMbos === '' },
    releaseFlashable: { invalid: flashingOrCoding && !((isMbos === YES && releaseFlashable !== '') || (isMbos !== YES && releaseFlashable === '')) },
    stillFlashable: { invalid: flashingOrCoding && !((isMbos === YES && releaseFlashable === NO && stillFlashable !== '') || (isMbos !== YES) || (isMbos === YES && releaseFlashable === YES)) },
  };
  const validationss = Object.values(mbosValidations).reduce((prev, current, index) => ({
    ...prev,
    [Object.keys(mbosValidations)[index]]: {
      ...current,
      error: current.invalid ? 'validations.required' : '',
      validations: { [Object.keys(mbosValidations)[index]]: { isValid: !current.invalid, error: current.invalid ? 'validations.required' : '' } },
    },
  }), {});

  return {
    invalid: Object.values(validationss).some(({ invalid }) => !!invalid),
    ...validationss,
  };
};

const validateRequestNumber = (requestNumber) => ({
  invalid: requestNumber?.length !== REQUIRED_SFM_LENGHT_NUMBER || !requestNumber?.match(/^[0-9]+$/),
  validations: { requestNumber: { isValid: requestNumber?.match(/^[0-9]+$/) && requestNumber?.length === REQUIRED_SFM_LENGHT_NUMBER, error: 'validations.required' } },
});

const validateOTAUpdateField = ({ otaUpdate, coding, flashing }) => ({
  invalid: (coding || flashing) && !otaUpdate,
  validations: { otaUpdate: { isValid: (coding || flashing) ? !!otaUpdate : true, error: 'validations.required' } },
});

const validateEECommissioningRequiredField = ({ eeCommissioningRequired, otaUpdate }) => ({
  invalid: (otaUpdate === NO) && !eeCommissioningRequired,
  validations: { eeCommissioningRequired: { isValid: (otaUpdate === NO) ? !!eeCommissioningRequired : true, error: 'validations.required' } },
});

const validateOtaUpdateCommentField = ({ otaUpdate, coding, flashing, otaComment, mbosDetails }, lang) => ({
  invalid: (coding || flashing) && isMbos(mbosDetails) && otaUpdate === NO ? (otaComment?.[lang] === '' || !otaComment?.[lang]) : false,
  validations: { otaComment: { isValid: otaComment?.[lang] === '' || !otaComment?.[lang], error: 'validations.required' } },
});

const validateSfmRequestedField = ({ flashingInformation, flashing }) => ({
  invalid: flashing && !flashingInformation.sfmRequested,
  validations: { sfmRequested: { isValid: flashing ? !!flashingInformation.sfmRequested : true, error: 'validations.required' } },
});

const validateSfmNumber = ({ requestNumber }) => {
  const sfmValidation = validateRequestNumber(requestNumber);

  return {
    requestNumber: sfmValidation,
    invalid: sfmValidation.invalid,
  };
};

const validateSfmNumbersFields = ({ flashingInformation }, isVans) => {
  const sfmNumberValidations = flashingInformation.sfm.map((sfmData) => validateSfmNumber(sfmData, isVans));

  return {
    ...sfmNumberValidations,
    invalid: Object.values(sfmNumberValidations || {}).some(({ invalid }) => invalid),
  };
};

const validateFlashingInformation = (affectedSoftware, isVans) => {
  const sfmRequestedValidation = validateSfmRequestedField(affectedSoftware);
  const sfmValidations = validateSfmNumbersFields(affectedSoftware, isVans);

  if (!sfmRequestedValidation.invalid) {
    return {
      sfmRequested: sfmRequestedValidation,
      sfm: sfmValidations,
      invalid: Object.values({ ...sfmRequestedValidation, ...sfmValidations }).some(({ invalid }) => invalid),
    };
  }

  return {
    sfmRequested: sfmRequestedValidation,
    invalid: true,
  };
};

export const validateSoftwareForm = (validations, campaign, lang, isVans = false) => {
  const { affectedSoftware: affSoftware } = campaign;
  const softwareFields = ['codingParts', 'flashingParts', 'doormanParts', SOFTWARE.OTA_UPDATE, 'flashingInformation', 'mbosDetails', 'eeCommissioningRequired'];

  if (((affSoftware?.flashing || affSoftware?.coding) && (affSoftware?.otaUpdate === NO && isMbos(affSoftware?.mbosDetails)))) {
    softwareFields.push(SOFTWARE.OTA_COMMENT);
  }
  const softwareValidation = {
    invalid: false,
    codingParts: validateCodingTable(affSoftware),
    flashingParts: validateFlashingTable(affSoftware),
    doormanParts: validateBlockVehicleTable(affSoftware),
    otaUpdate: validateOTAUpdateField(affSoftware),
    otaComment: validateOtaUpdateCommentField(affSoftware, lang),
    mbosDetails: validateMBOSDetails(affSoftware),
    flashingInformation: validateFlashingInformation(affSoftware, isVans),
    eeCommissioningRequired: validateEECommissioningRequiredField(affSoftware),
    codingUuids: validateCodingTable(affSoftware),
  };
  softwareValidation.invalid = softwareFields.some((field) => softwareValidation[field]?.invalid);

  return softwareValidation;
};

const getRecipientsCodes = (info, recipients) => ({
  creator: info.creationUser?.code,
  requester: recipients.responsibleRequester?.code,
  standin: recipients.standIn?.code,
  causerApprover: recipients.causerApprover?.code,
  causerPreApprover: recipients.causerPreApprover?.code,
  causerCheckApprover: recipients.causerCheckApprover?.code,
  informerApprover: recipients.informerApprover?.code,
  informerPreApprover: recipients.informerPreApprover?.code,
  informerCheckApprover: recipients.informerCheckApprover?.code,
  E3ChiefEngineer: recipients?.e3ChiefEngineer?.code,
});

export const validateRecipientsForm = (validations, campaign) => {
  const { info, recipients } = campaign;
  const isVANCR = isVansBeforeFlowRemoval(campaign?.info?.type, campaign?.info?.creationDate);
  const { E2I_E2C, E4I_E3I_E2I_E2C } = workflowOptions;
  const { creator, requester, standin, causerApprover, causerPreApprover, causerCheckApprover, informerApprover, informerPreApprover, informerCheckApprover, E3ChiefEngineer } = getRecipientsCodes(info, recipients);
  const mainResponsibles = [creator, requester, standin].filter((responsible) => responsible);
  const approvers = [causerApprover, causerPreApprover, causerCheckApprover, informerApprover, informerPreApprover, informerCheckApprover]
    .filter((approver) => approver);

  const recipientsValidation = {
    sameStandinCreator: (creator && creator === standin),
    sameStandinRequester: (standin && standin === requester),
    sameApprover: approvers.some((approver, index) => index !== approvers.indexOf(approver)),
    sameMainResponsibleApprover: mainResponsibles.some((responsible) => approvers.includes(responsible)),
  };

  if (isVANCR) {
    recipientsValidation.sameE3ChiefEngineer = [causerApprover, informerApprover, standin, creator].some((approver) => (approver === E3ChiefEngineer && approver));
  }

  recipientsValidation.fourEyesInvalid = Object.values(recipientsValidation).some((invalidRule) => invalidRule) && !isVANCR; // No 4eyes validation if the CR is VAN
  recipientsValidation.incorrectOrder = (((causerCheckApprover && (!causerApprover || !causerPreApprover)) || (causerPreApprover && !causerApprover)
    || (informerCheckApprover && (!informerApprover || informerPreApprover)) || (informerPreApprover && !informerApprover)) && !isVANCR); // No order validation if the CR is VAN

  let recipientsFields = [];

  if (campaign.info.type === CR_TYPE.CAR) {
    recipientsFields = recipients?.approvalWorkflow === workflowOptions.E4C_E3C_E2C
      ? ['approvalWorkflow', 'responsibleRequester', 'standIn', 'causerApprover', 'causerPreApprover', 'causerCheckApprover']
      : ['approvalWorkflow', 'responsibleRequester', 'standIn', 'causerApprover'];

    if (recipients?.approvalWorkflow === E2I_E2C) recipientsFields.push('informerApprover');
    if (recipients?.approvalWorkflow === E4I_E3I_E2I_E2C) recipientsFields.push('informerPreApprover', 'informerCheckApprover', 'informerApprover');

    if (campaign.info.status === 'IN_CREATION'
      && isCampaignRDMP(campaign.info.creationUser.department)
      && campaign.info.version >= 2) recipientsFields.push('commentsRdCampaignManager');

    recipientsFields.forEach((field) => {
      const value = ['approvalWorkflow', 'commentsRdCampaignManager'].includes(field) ? recipients?.[field] : recipients?.[field]?.code;
      recipientsValidation[field] = validateField(validations, value, defineErrorsTypes(field));
    });
  } else if (isVANCR) {
    recipientsFields = ['approvalWorkflow', 'responsibleRequester', 'standIn', 'informerCheckApprover', 'causerPreApprover',
      'e3ChiefEngineer', RECIPIENTS.E3_QMB_APPROVER];

    if (campaign.info.riskPotentiallyRelevant) recipientsFields.push(RECIPIENTS.E2_QMB_INFORMER);

    recipientsFields.forEach((field) => {
      if (field === RECIPIENTS.E3_QMB_APPROVER) {
        recipientsValidation[field] = {};
        Object.keys(recipients?.e3QMBApprover).forEach((plant) => {
          const value = recipients?.e3QMBApprover?.[plant]?.code;
          recipientsValidation[field][plant] = validateField(validations, value, defineErrorsTypes(field));
        });
      } else {
        const value = ['approvalWorkflow'].includes(field) ? recipients?.[field] : recipients?.[field]?.code;
        recipientsValidation[field] = validateField(validations, value, defineErrorsTypes(field));
      }
    });
  }

  recipientsValidation.someEmptyField = recipientsFields.some((field) => recipientsValidation[field]?.invalid)
    || Object.values(recipientsValidation.e3QMBApprover ?? {}).some((user) => user.invalid === true);

  recipientsValidation.invalid = recipientsValidation.someEmptyField || recipientsValidation.fourEyesInvalid;

  return recipientsValidation;
};

export const validateVehiclestranslations = (modules, defaultLanguage, lang) => {
  const affectedVehiclesModules = modules?.AFFECTED_VEHICLES?.translatablePropertiesByPlant || [];
  const validationsByPlant = affectedVehiclesModules
    .reduce((accumulator, properties) => {
      const fieldsWithValues = properties.translatableProperties.filter((field) => field.translations?.[defaultLanguage]);
      const groupFieldsWithValues = properties.translatablePropertiesByGroup
        .reduce((acc, { translatableProperties }) => [...acc, ...translatableProperties], [])
        .filter((field) => field.translations?.[defaultLanguage]);

      const fieldsValidations = [...fieldsWithValues, ...groupFieldsWithValues].reduce((accValidations, field) => ({
        ...accValidations,
        [okNokFields.includes(field.code) ? field.uuid : field.code]: !field.translations[lang],
        invalid: Object.values(accValidations).some((validation) => validation) || !field.translations[lang],
      }), { invalid: false });

      return {
        ...accumulator,
        [properties.plant.wkzNumber]: fieldsValidations,
      };
    }, { invalid: false });

  validationsByPlant.invalid = Object.values(validationsByPlant).some(({ invalid }) => invalid);

  return validationsByPlant;
};

const validateCampaignTranslations = (campaign, validate, defaultLanguage, lang) => {
  const modules = campaign.translations?.modules;
  const translationsFields = ['campaignTitle', 'aggregateComment', 'countryVariantsComment', 'otherDescriptions',
    'paintCodeComment', 'specialEquipmentComment', 'textDate', 'vehicleModelDesignationComment', 'consultationComment',
    CLASSIFICATION.RELEVANCE_COMMENT, 'consequences', 'immediateMeasure', DESCRIPTION.INSPECTION_COMMENT, 'problemDescription', 'rootCause',
    DESCRIPTION.TRIGGER_COMMENT, 'comment', 'commentsApproverCauser', 'commentsApproverInformer', 'ioIdentification', SOFTWARE.OTA_COMMENT,
    'affectedVehicles'];

  const translationValidation = {
    invalid: false,
    campaignTitle: validate(modules?.CAMPAIGN, campaign.info.title, 'campaignTitle'),
    consultationComment: validate(modules?.CLASSIFICATION, campaign.classification.consultationComment, 'consultationComment'),
    relevanceComment: validate(modules?.CLASSIFICATION, campaign.classification.relevanceComment, CLASSIFICATION.RELEVANCE_COMMENT),
    consequences: validate(modules?.DESCRIPTION, campaign.description.problems?.CONSEQUENCES, 'consequences'),
    immediateMeasure: validate(modules?.DESCRIPTION, campaign.description.problems?.IMMEDIATE_MEASURE, 'immediateMeasure'),
    inspectionComment: validate(modules?.DESCRIPTION, campaign.description.inspectionComment, DESCRIPTION.INSPECTION_COMMENT),
    problemDescription: validate(modules?.DESCRIPTION, campaign.description.problems?.PROBLEM_DESCRIPTION, 'problemDescription'),
    rootCause: validate(modules?.DESCRIPTION, campaign.description.problems?.ROOT_CAUSE, 'rootCause'),
    triggerComment: validate(modules?.DESCRIPTION, campaign.description.triggerComment, DESCRIPTION.TRIGGER_COMMENT),
    comment: validate(modules?.DESCRIPTION, campaign.description.comment, 'comment'),
    commentsApproverCauser: validate(modules?.RECIPIENTS, campaign.recipients.commentsApproverCauser, 'commentsApproverCauser'),
    commentsApproverInformer: validate(modules?.RECIPIENTS, campaign.recipients.commentsApproverInformer, 'commentsApproverInformer'),
    ioIdentification: validate(modules?.AFFECTED_HARDWARE, campaign.affectedHardware.partAvailability?.ioIdentification, 'ioIdentification'),
    otaComment: validate(modules?.AFFECTED_SOFTWARE, campaign.affectedSoftware?.otaComment, SOFTWARE.OTA_COMMENT),
    affectedVehicles: validateVehiclestranslations(modules, defaultLanguage, lang),
  };

  translationValidation.invalid = translationsFields.some((field) => translationValidation[field]?.invalid);

  return translationValidation;
};

export const validateTranslationsForm = (campaign, lang) => {
  const { defaultLanguage, activeLanguages } = campaign.info;

  if (activeLanguages.length === 1 || lang === defaultLanguage) {
    return { invalid: false };
  }

  const validateFieldTranslation = (translations, campaignValue, label) => {
    const fieldOriginal = campaignValue?.[defaultLanguage];
    const field = translations?.translatableProperties?.[label]?.translations?.[lang];
    const invalid = !!fieldOriginal && field === '';

    return { invalid };
  };

  return validateCampaignTranslations(campaign, validateFieldTranslation, defaultLanguage, lang);
};

const validateNokOk = (validations, value, defaultLanguage, key, section) => {
  if (key === 'dates') {
    const fieldsValidation = {
      textDate: validateField(validations, value.calendarDate, defineErrorsTypes('nokOk')),
      time: validateField(validations, value.timeDate, defineErrorsTypes('nokOk')),
      referenceDate: validateField(validations, value.textDate.translations?.[defaultLanguage], defineErrorsTypes('nokOk')),
    };

    return {
      ...fieldsValidation,
      invalid: Object.values(fieldsValidation).some(({ invalid }) => invalid),
    };
  }

  if (key === 'otherDescriptions') {
    return validateField(validations, value.translations[defaultLanguage], defineErrorsTypes('nokOk'));
  }

  if (key === 'releases') {
    const fieldsValidation = {
      moreUpcomingReleases: section === 'cleanPoint'
        ? { invalid: false }
        : validateField(validations, value.moreUpcomingReleases, defineErrorsTypes('nokOk')),
    };

    return {
      ...fieldsValidation,
      invalid: Object.values(fieldsValidation).some(({ invalid }) => invalid),
    };
  }

  return validateField(validations, value, defineErrorsTypes('nokOk'));
};

const validateVolumeWithFileOption = (validations = {}, volume = {}) => ({
  ...validations,
  volumes: {
    ...validations.volumes,
    [volume.plant]: { invalid: volume.vehicleRestrictionFiles.length === 0 },
  },
});

const validateVolumeWithManualEntry = (validations = {}, volume = {}, limitationPlant = {}, lang) => {
  const sectioCleanDirt = ['dirtPoint', 'cleanPoint'];

  const validateOneField = (fieldAcc, fieldValue, key, index, section) => ({
    ...fieldAcc,
    [index]: validateNokOk(validations, fieldValue, lang, key, section),
  });

  const validateOptionFields = (fieldsAcc, key, volumeSelected, section) => {
    const fieldValidations = (volumeSelected?.[key] || [])
      .reduce((fieldAcc, fieldValue, index) => validateOneField(fieldAcc, fieldValue, key, index, section), {});

    return {
      ...fieldsAcc,
      [key]: {
        ...fieldValidations,
        invalid: Object.values(fieldValidations).some(({ invalid }) => invalid),
      },
    };
  };

  const validateCleanDirtBySection = (sectionAcc, section) => {
    let isInvalid = false;
    const response = {
      ...sectionAcc,
      invalid: false,
      vehiclesCleanDirt: {
        ...sectionAcc.vehiclesCleanDirt,
        invalid: false,
      },
    };

    const vehicleGroups = volume?.limitationVolumeManualEntry?.vehicleGroups || [];

    vehicleGroups.forEach((group, index) => {
      const volumeSelected = group?.[section] || {};
      const volumeValidations = Object.keys(volumeSelected || {})
        .reduce((fieldAcc, key) => validateOptionFields(fieldAcc, key, volumeSelected, section), {});

      const sectionIsEmpty = Object.values(volumeSelected || {}).flat().length === 0;
      const someMissingField = Object.values(volumeValidations).some(({ invalid }) => invalid);
      const invalid = sectionIsEmpty || someMissingField;
      isInvalid = isInvalid || invalid;

      response.vehiclesCleanDirt = {
        ...response.vehiclesCleanDirt,
        [index]: {
          ...response.vehiclesCleanDirt?.[index],
          [section]: {
            ...volumeValidations,
            invalid,
          },
        },
      };
    });

    response.vehiclesCleanDirt.invalid = Object.values(response.vehiclesCleanDirt).some(
      ({ dirtPoint, cleanPoint }) => dirtPoint?.invalid || cleanPoint?.invalid,
    );

    return response;
  };

  const validateVehicleGroups = () => {
    const allMSPlant = getAllModelSeries(limitationPlant);
    const selectedMSPlant = getSelectedModelSeries(volume);

    return {
      allVehiclesInGroups: {
        invalid: Boolean(allMSPlant.length !== selectedMSPlant.length),
      },
    };
  };

  const validateRule = (rule) => {
    const ruleValidation = {
      codeRulesSelectionAccording: validateField(validations, 'not-included', defineErrorsTypes('codeRule')),
      codeRulesDescription: validateField(validations, rule.codeRulesDescription, defineErrorsTypes('codeRule')),
      codeRuleComment: validateField(validations, rule.codeRuleComment?.translations?.[lang], defineErrorsTypes('codeRuleComment')),
    };

    return {
      ...ruleValidation,
      invalid: Object.values(ruleValidation || {}).some(({ invalid }) => invalid),
    };
  };

  const validateCodeRulesTab = () => {
    const { codeRules } = volume.limitationVolumeManualEntry;
    const codeRulesValidations = codeRules?.map(validateRule);
    const someRuleAdded = codeRules?.length > 0;

    return {
      codeRules: {
        ...codeRulesValidations,
        invalid: someRuleAdded && Object.values(codeRulesValidations || {}).some(({ invalid }) => invalid),
      },
    };
  };

  const validateVolumeTab = (tab, comment) => {
    const tabValidation = {
      [`${tab}Comment`]: validateField(validations, comment?.[lang], defineErrorsTypes(`${tab}Comment`)),
      [`${tab}Selecteds`]: validateField(validations, 'not-included', defineErrorsTypes(`${tab}Selecteds`)),
      [`${tab}SelectionAccording`]: validateField(validations, 'not-included', defineErrorsTypes(`${tab}SelectionAccording`)),

    };

    return {
      [tab]: {
        ...tabValidation,
        invalid: Object.values(tabValidation || {}).some(({ invalid }) => invalid),
      },
    };
  };

  const validateVolumeTabs = () => {
    const tabs = Object.keys(limitOfVolumeTabsMapping).filter((tab) => !['summary', 'vehicleGroups', 'codeRules'].includes(tab));
    const { limitationVolumeManualEntry } = volume;

    return tabs.reduce((accTabs, currTab) => ({
      ...accTabs,
      ...validateVolumeTab(currTab, limitationVolumeManualEntry?.[`${currTab}Comment`],
        limitationVolumeManualEntry?.[`${currTab}Selecteds`], limitationVolumeManualEntry?.[`${currTab}SelectionAccording`]),
    }), {});
  };

  const vehicleGroupsValidations = validateVehicleGroups();
  const cleanDirtValidations = sectioCleanDirt.reduce((acc, section) => validateCleanDirtBySection(acc, section), {});
  cleanDirtValidations.vehiclesCleanDirt.invalid = cleanDirtValidations.vehiclesCleanDirt.invalid
    || vehicleGroupsValidations.allVehiclesInGroups.invalid;
  const codeRulesValidations = validateCodeRulesTab();
  const volumeTabsValidations = validateVolumeTabs();
  const volumeValidation = { ...cleanDirtValidations, ...vehicleGroupsValidations, ...codeRulesValidations, ...volumeTabsValidations };

  return {
    ...validations,
    volumes: {
      ...validations?.volumes,
      [volume?.plant]: {
        ...volumeValidation,
        invalid: Object.values(volumeValidation || {}).some(({ invalid }) => invalid),
      },
    },
  };
};

export const validateVolume = (validations, volume, limitationPlant, lang) => (
  volume?.fileOptionSelected
    ? validateVolumeWithFileOption(validations, volume)
    : validateVolumeWithManualEntry(validations, volume, limitationPlant, lang));

export const validateAffectedVehiclesForm = (campaign, headerPlants, lang) => {
  let affectedVehiclesValidation = { volumes: {}, seriesInvalid: false, invalid: false };

  const { affectedPlants, volumes, series } = campaign.affectedVehicles;
  const { limitationPlants } = campaign;
  const plants = (isViaModelSeries(campaign.affectedVehicles)
    ? headerPlants
    : affectedPlants) || [];

  const validateSeries = () => {
    if (isViaPlants(campaign.affectedVehicles) ? affectedPlants?.length === 0 : series?.length === 0) {
      affectedVehiclesValidation = { volumes: {}, seriesInvalid: true, invalid: true };
    }
  };

  const validateVolumes = () => {
    const plantCodes = plants.map(({ wkzNumber }) => wkzNumber);
    const volumePlantCodes = new Set((volumes || []).map(({ plant }) => plant));

    plantCodes
      .filter((wkzNumber) => !volumePlantCodes.has(wkzNumber))
      .forEach((wkzNumber) => {
        affectedVehiclesValidation = {
          ...affectedVehiclesValidation,
          volumes: {
            ...affectedVehiclesValidation.volumes,
            [wkzNumber]: { invalid: true },
          },
        };
      });

    (volumes || []).forEach((currentVolume) => {
      if (plantCodes.includes(currentVolume.plant)) {
        const limitationPlant = limitationPlants?.[currentVolume?.plant];
        affectedVehiclesValidation = validateVolume(affectedVehiclesValidation, currentVolume, limitationPlant, lang);
      }
    });
  };

  const validateCompleteTab = () => {
    affectedVehiclesValidation.invalid = Object.values(affectedVehiclesValidation.volumes || {})
      .some(({ invalid }) => invalid) || affectedVehiclesValidation.seriesInvalid;

    return affectedVehiclesValidation;
  };

  validateSeries();
  validateVolumes();

  return validateCompleteTab();
};

export const getErrorMessage = (errors) => {
  if (errors?.required) {
    return 'validations.required';
  }

  if (errors?.min) {
    return 'validations.commentsMinLength';
  }

  if (errors?.max) {
    return 'validations.maxLength';
  }

  return '';
};
